<template>
  <BaseModal
    :modal-open="modalOpen"
    max-width="700px"
    backgroundHeader="transparent"
    @close="closeModal"
  >
    <template #header>
      <v-row class="align-top pt-2" justify="space-between">
        <v-col sm="10">
          <v-row no-gutters justify="center" class="text-left">
            <v-col no-gutters>
              <h5>New Affiliation Information</h5>
            </v-col>
          </v-row>
        </v-col>
        <v-btn
          elevation="1"
          @click="closeModal"
          icon="mdi-close"
          variant="text"
          :color="eventColor || 'info'"
          size="small"
        ></v-btn>
      </v-row>
    </template>
    <template #default>
      <v-row no-gutters class="pa-4">
        <v-col>
          <v-row no-gutters>
            <label for="new-affiliation-name" class="d-block mb-3"
              >Affiliation Name <span style="color: red">*</span></label
            >
          </v-row>
          <v-row no-gutters>
            <v-text-field
              v-model="newAffiliationName"
              id="new-affiliation-name"
              density="compact"
              variant="outlined"
              hide-details
              @focusout="postValidateAffiliateNameIsAvailable"
              :rules="[(val) => (val ? true : 'Required')]"
              :disabled="isCheckingAffiliationNameValidity"
            >
              <template #append-inner>
                <v-progress-circular
                  color="primary"
                  indeterminate
                  size="20"
                  v-if="isCheckingAffiliationNameValidity"
                />
                <v-icon
                  icon="mdi-check-bold"
                  color="success"
                  v-else-if="validAffiliationName === true"
                />
                <v-icon icon="mdi-alert" color="error" v-else-if="validAffiliationName === false" />
              </template>
            </v-text-field>
          </v-row>
          <v-row no-gutters class="mt-4">
            <v-col>
              <label for="new-affiliation-address-1" class="d-block mb-3"
                >Address 1 <span style="color: red">*</span></label
              >
              <v-text-field
                v-model="newAffiliationAddress1"
                id="new-affiliation-address-1"
                density="compact"
                variant="outlined"
                hide-details
                :rules="[(val) => (val ? true : 'Required')]"
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-4">
            <v-col>
              <label for="new-affiliation-address-2" class="d-block mb-3">Address 2</label>
              <v-text-field
                v-model="newAffiliationAddress2"
                id="new-affiliation-address-2"
                density="compact"
                variant="outlined"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row class="mt-4 ga-4" no-gutters>
            <v-col>
              <label for="new-affiliation-city" class="d-block mb-3"
                >City <span style="color: red">*</span></label
              >
              <v-text-field
                v-model="newAffiliationCity"
                id="new-affiliation-city"
                density="compact"
                variant="outlined"
                hide-details
                :rules="[(val) => (val ? true : 'Required')]"
              />
            </v-col>
            <v-col>
              <label for="new-affiliation-state" class="d-block mb-3"
                >State <span style="color: red">*</span></label
              >
              <v-select
                v-model="newAffiliationState"
                :items="newAffiliationCountry?.states ?? []"
                item-title="name"
                item-value="name"
                id="new-affiliation-state"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row class="mt-4 ga-4" no-gutters>
            <v-col>
              <label for="new-affiliation-postal-code" class="d-block mb-3"
                >Zip Code <span style="color: red">*</span></label
              >
              <v-text-field
                v-model="newAffiliationPostalCode"
                id="new-affiliation-postal-code"
                density="compact"
                variant="outlined"
                hide-details
                :rules="[(val) => (val ? true : 'Required')]"
              />
            </v-col>
            <v-col>
              <label for="new-affiliation-country" class="d-block mb-3">Country</label>
              <v-select
                v-model="newAffiliationCountry"
                :items="countries"
                item-title="acronym"
                return-object
                id="new-affiliation-country"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row class="mt-4 ga-4" no-gutters>
            <v-col>
              <label for="new-affiliation-first-name" class="d-block mb-3"
                >Point of Contact First Name</label
              >
              <v-text-field
                v-model="newAffiliationFirstName"
                id="new-affiliation-first-name"
                density="compact"
                variant="outlined"
                hide-details
              />
            </v-col>
            <v-col>
              <label for="new-affiliation-last-name" class="d-block mb-3"
                >Point of Contact Last Name</label
              >
              <v-text-field
                v-model="newAffiliationLastName"
                id="new-affiliation-last-name"
                density="compact"
                variant="outlined"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-4">
            <v-col>
              <label for="new-affiliation-email" class="d-block mb-3"
                >Contact Email <span style="color: red">*</span></label
              >
              <v-text-field
                v-model="newAffiliationEmail"
                id="new-affiliation-email"
                density="compact"
                variant="outlined"
                hide-details
                placeholder="example@fundeasy.com"
                prepend-inner-icon="mdi-email"
                :rules="[(val) => (isValidEmail(val) ? true : 'Valid email is required')]"
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-4">
            <v-col>
              <label for="new-affiliation-phone" class="d-block mb-3">Phone</label>
              <v-text-field
                id="new-affiliation-phone"
                prepend-inner-icon="mdi-phone"
                variant="outlined"
                density="compact"
                placeholder="(777) 777 - 7777"
                :model-value="
                  newAffiliationPhone ? phoneMask.masked(String(newAffiliationPhone)) : ''
                "
                @update:modelValue="handleAffiliationPhone"
                :rules="[
                  (val) => {
                    const digits = phoneMask.unmasked(String(val || '').replace(/[^0-9]/g, ''));
                    if (!digits) return true;
                    return digits.length === 10 ? true : 'Phone number must be 10 digits';
                  },
                ]"
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-8" justify="end">
            <v-btn
              elevation="1"
              :color="eventColor || 'info'"
              :disabled="!canContinueNewAffiliation || isCreatingAffiliation"
              @click="storeAffiliationDetails"
            >
              <v-progress-circular
                v-if="isCreatingAffiliation"
                indeterminate
                size="18"
                width="2"
                class="mr-2"
              />
              Save
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </BaseModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { Mask } from 'maska';
import countries from '@/api/countries';
import BaseModal from '@/components/modals/BaseModal.vue';
import BaseUrl from '@/api/BaseUrl';
import ENDPOINTS from '@/api/endpoints';
import { useSnackbarStore } from '@/stores/snackbarInfo';
import { isValidEmail } from '@/utils/validators';

const props = defineProps({
  modalOpen: {
    type: Boolean,
    default: false,
  },
  eventColor: {
    type: String,
    default: 'info',
  },
});

const emit = defineEmits(['close', 'created']);

const route = useRoute();
const snackbar = useSnackbarStore();
const phoneMask = new Mask({ mask: '(###) ###-####' });

const newAffiliationName = ref('');
const newAffiliationAddress1 = ref('');
const newAffiliationAddress2 = ref('');
const newAffiliationCity = ref('');
const newAffiliationState = ref('');
const newAffiliationPostalCode = ref('');
const newAffiliationCountry = ref(countries[0]);
const newAffiliationFirstName = ref('');
const newAffiliationLastName = ref('');
const newAffiliationEmail = ref('');
const newAffiliationPhone = ref('');
const validAffiliationName = ref(null);
const isCheckingAffiliationNameValidity = ref(false);
const isCreatingAffiliation = ref(false);

const canContinueNewAffiliation = computed(
  () =>
    !!newAffiliationName.value &&
    !!newAffiliationAddress1.value &&
    !!newAffiliationCity.value &&
    !!newAffiliationState.value &&
    !!newAffiliationPostalCode.value &&
    isValidEmail(newAffiliationEmail.value) &&
    (!newAffiliationPhone.value || newAffiliationPhone.value.length === 10) &&
    !isCheckingAffiliationNameValidity.value &&
    validAffiliationName.value !== false,
);

const resetForm = () => {
  newAffiliationName.value = '';
  newAffiliationAddress1.value = '';
  newAffiliationAddress2.value = '';
  newAffiliationCity.value = '';
  newAffiliationState.value = '';
  newAffiliationPostalCode.value = '';
  newAffiliationCountry.value = countries[0];
  newAffiliationFirstName.value = '';
  newAffiliationLastName.value = '';
  newAffiliationEmail.value = '';
  newAffiliationPhone.value = '';
  validAffiliationName.value = null;
  isCheckingAffiliationNameValidity.value = false;
  isCreatingAffiliation.value = false;
};

const closeModal = () => {
  resetForm();
  emit('close');
};

const handleAffiliationPhone = (value) => {
  newAffiliationPhone.value = phoneMask.unmasked(String(value || '').replace(/[^0-9]/g, ''));
};

const postValidateAffiliateNameIsAvailable = async () => {
  const affiliationNameValidationUrl = `${BaseUrl.get(ENDPOINTS.EVENTS.POST_VALIDATE_AFFILIATION_NAME)}${
    ENDPOINTS.EVENTS.POST_VALIDATE_AFFILIATION_NAME
  }?event_id=${route.params.id}`;
  validAffiliationName.value = null;
  isCheckingAffiliationNameValidity.value = true;
  const { error, execute: validateAffiliationName } = useFetch(
    affiliationNameValidationUrl,
    {
      headers: Headers,
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ name: newAffiliationName.value }),
    },
    {
      immediate: false,
    },
  );
  await validateAffiliationName();
  if (error.value) {
    snackbar.setSnackbar('Please try different Affiliation Name!', 'error', 2000);
  }
  validAffiliationName.value = !error.value;
  isCheckingAffiliationNameValidity.value = false;
};

const storeAffiliationDetails = async () => {
  const affiliationUrl = `${BaseUrl.get(ENDPOINTS.EVENTS.CREATE_AFFILIATION)}${
    ENDPOINTS.EVENTS.CREATE_AFFILIATION
  }?event_id=${route.params.id}`;

  isCreatingAffiliation.value = true;
  const { data, error } = await useFetch(
    affiliationUrl,
    {
      headers: Headers,
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        name: newAffiliationName.value,
        address1: newAffiliationAddress1.value,
        address2: newAffiliationAddress2.value,
        city: newAffiliationCity.value,
        state: newAffiliationState.value,
        postal_code: newAffiliationPostalCode.value,
        country_code: newAffiliationCountry.value?.acronym,
        first_name: newAffiliationFirstName.value,
        last_name: newAffiliationLastName.value,
        email: newAffiliationEmail.value,
        phone_number: newAffiliationPhone.value || null,
      }),
    },
    {
      immediate: false,
    },
  );

  isCreatingAffiliation.value = false;
  if (error.value) {
    snackbar.setSnackbar('Error creating Affiliation!', 'error', 2000);
    return;
  }

  const createdAffiliationData = data.value?.result;
  if (createdAffiliationData?.name) {
    emit('created', {
      id: createdAffiliationData.id,
      name: createdAffiliationData.name,
    });
    snackbar.setSnackbar('Affiliation created!', 'success', 2000);
    closeModal();
  }
};

watch(
  () => props.modalOpen,
  (isOpen) => {
    if (!isOpen) {
      resetForm();
    }
  },
);
</script>
